import Pagination from 'components/Blocks/Pagination/Pagination';
import { ProductReviewItem } from 'components/Blocks/Reviews/ProductReviewItem';
import Button from 'components/Forms/Button/Button';
import { useReviews } from 'connectors/reviews/Reviews';
import useTranslation from 'next-translate/useTranslation';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { RefObject, useCallback, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { ReviewConnectionType, REVIEWS_DEFAULT_PAGE_SIZE, ReviewType } from 'types/review';

type ProductReviewsListProps = {
    shouldDisplay?: boolean;
    productUuid: string;
    paginationScrollTargetRef: RefObject<HTMLDivElement>;
    increaseParentHeightCounter?: () => void;
};

export const ProductReviewsList = ({
    productUuid,
    paginationScrollTargetRef,
    increaseParentHeightCounter,
    shouldDisplay = false,
}: ProductReviewsListProps) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const productReviewsResult: ReviewConnectionType = useReviews(currentPage, productUuid);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    /*
     * Trigger collapse height calculation when layout changes
     * (otherwise the collapsed block may not be fully displayed)
     */
    const containerRef = useCallback(
        (node: Element | null) => {
            if (!node || !increaseParentHeightCounter) {
                return undefined;
            }

            let lastHeight = node.clientHeight;

            const resizeObserver = new ResizeObserver(() => {
                const newHeight = node.clientHeight;
                if (newHeight !== lastHeight) {
                    increaseParentHeightCounter();
                    lastHeight = newHeight;
                }
            });

            resizeObserver.observe(node);

            return undefined;
        },
        [increaseParentHeightCounter],
    );

    const displayedProductReviews = productReviewsResult.reviews.filter((review: ReviewType, index) => {
        return isExpanded || index < 2;
    });

    return (
        <div
            ref={containerRef}
            className={twJoin(
                'relative opacity-0 transition-opacity delay-150 duration-300 ease-in-out',
                shouldDisplay && 'opacity-100',
            )}
        >
            {displayedProductReviews.map((review: ReviewType, index) => {
                return <ProductReviewItem key={`review-${index}`} review={review} />;
            })}
            {!isExpanded && productReviewsResult.totalCount > 2 && (
                <>
                    <div className="absolute left-0 right-0 top-0 h-full w-full bg-gradient-to-t from-white vl:from-[#f7f7f7]" />
                    <div className="absolute bottom-4 left-1/2 z-two -translate-x-1/2 transform">
                        <Button size="small" onClick={() => setIsExpanded(true)}>
                            <ArrowIcon className="mr-2 h-3 w-3" />
                            {t('Show more reviews')}
                        </Button>
                    </div>
                </>
            )}
            {isExpanded && (
                <Pagination
                    pageSize={REVIEWS_DEFAULT_PAGE_SIZE}
                    paginationScrollTargetRef={paginationScrollTargetRef}
                    pushPageChangeToQueryParams={false}
                    totalCount={productReviewsResult.totalCount}
                    onPageChangeEvent={(page) => setCurrentPage(page)}
                />
            )}
        </div>
    );
};
